import { Controller, FormProvider, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslations } from "use-intl";
import { type HotelBooking } from "@/app/lib/types/types";
import type {
  FindHotelsOutputData,
  FindUserTravelersTravelers,
} from "@/app/lib/types/codegen";
import { usePaymentContext } from "@/app/lib/context/PaymentContext";
import { formatCurrency } from "@/app/lib/utils/currency";
import {
  includeBnplFee,
  calculateMonthlyInstallment,
} from "@/app/lib/utils/bnpl-fee";
import { getTravellerTypeAtTravelDate } from "@/app/lib/utils/ageValidation";
import { HotelAndRoomCard } from "./HotelAndRoomCard";
import BottomDrawer from "../flight-purchase/BottomDrawer";
import InstallmentDetails from "../flight-purchase/InstallmentDetails";
import { MainTravellerRadioButtons } from "./MainTravellerRadioButtons";
import { TermsAndConditions } from "./TermsAndConditions";
import { BannersPlacement } from "../banners-placement/BannersPlacement";
import { BannersPlacementSlug } from "@/app/lib/types/enums";

export interface SummaryFormValues {
  roomAssignments: Record<string, { travelers: string[] }>;
  leadTraveler: string;
  termsAndConditions: boolean;
}

export interface HotelBookingSummaryFormProps {
  booking: HotelBooking;
  travelDate: Date;
  hotel: FindHotelsOutputData;
  loading: boolean;
  disabledSubmit?: boolean;
  rooms?: { adults: number; children: number }[];
  buttonLabel: string;
  selectedTravellers: FindUserTravelersTravelers[];
  handleSubmit: (values: SummaryFormValues) => void;
}

export const HotelBookingSummaryForm = ({
  booking,
  travelDate,
  hotel,
  loading,
  rooms = [],
  buttonLabel,
  disabledSubmit,
  selectedTravellers,
  handleSubmit: onSubmit,
}: HotelBookingSummaryFormProps) => {
  const t = useTranslations();
  const { paymentType, selectedInstallments } = usePaymentContext();

  const hotelTotalPrice =
    booking?.rooms.reduce((acc, room) => acc + (room.price?.value || 0), 0) ||
    0;

  const totalPrice =
    paymentType === "bnpl" ? includeBnplFee(hotelTotalPrice) : hotelTotalPrice;

  const validationSchema = z.object({
    termsAndConditions: z.boolean().refine((v) => v === true),
    leadTraveler: z.string().min(1),
    roomAssignments: z
      .object(
        Object.fromEntries(
          booking.rooms.map((room, index) => [
            room.roomToken,
            z.object({
              travelers: z
                .array(z.string())
                .length(rooms[index].adults + rooms[index].children),
            }),
          ]),
        ),
      )
      .required(),
  });

  const leadTraveler = selectedTravellers.find(
    (traveller) =>
      getTravellerTypeAtTravelDate(traveller.birthDate, travelDate) === "adult",
  );

  const methods = useForm<
    z.input<typeof validationSchema>,
    undefined,
    z.infer<typeof validationSchema>
  >({
    defaultValues: {
      roomAssignments:
        booking.rooms.length === 1
          ? {
              [booking.rooms[0].roomToken]: {
                travelers: selectedTravellers.map((traveller) => traveller.id),
              },
            }
          : {},
      leadTraveler: leadTraveler?.id || "",
      termsAndConditions: false,
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    register,
    control,
    handleSubmit,
    formState: { isValid, isDirty },
  } = methods;

  return (
    <FormProvider {...methods}>
      <div className="flex w-full flex-col">
        <HotelAndRoomCard
          hotel={hotel}
          hotelBookingData={booking}
          rooms={rooms}
        />
        <div className="section-title mt-4">
          {t("travellers.mainTraveller")}
        </div>
        <div className="section-card p-0">
          <div className="p-4 pb-2 text-sm text-text-tertiary">
            {t("travellers.markMainTraveller")}
          </div>

          <Controller
            control={control}
            name="leadTraveler"
            render={({ field }) => (
              <MainTravellerRadioButtons
                {...field}
                travelDate={travelDate}
                selectedTravellers={selectedTravellers}
              />
            )}
          />
        </div>

        <div className="section-card mt-2">
          <div className="text-sm text-text-title">
            {t("hotels.bookingPolicy")}
          </div>
        </div>

        <BannersPlacement
          slug={BannersPlacementSlug.CHECKOUT}
          classNames={{
            wrapper: "-mx-4 mt-4",
          }}
        />

        <div className="section-title mt-4">{t("summary")}</div>
        <div className="section-card">
          <TermsAndConditions {...register("termsAndConditions")} />
        </div>

        {paymentType === "bnpl" ? (
          <InstallmentDetails totalPrice={totalPrice} />
        ) : (
          <div className="section-card mt-2 flex flex-col gap-2 text-sm">
            <div className="flex justify-between">
              <div className="text-text-tertiary">{t("total")}</div>
              <div className="font-bold text-text-primary">
                {formatCurrency(totalPrice, booking?.price?.currency)}
              </div>
            </div>
            <div className="flex justify-between">
              <div className="text-text-tertiary">{t("paymentMethod")}</div>
              <div className="font-bold text-text-primary">{t("qiCard")}</div>
            </div>
          </div>
        )}
      </div>

      <BottomDrawer
        loading={loading}
        totalPrice={
          paymentType === "bnpl" ? undefined : formatCurrency(totalPrice)
        }
        payLaterPrice={
          paymentType === "bnpl"
            ? formatCurrency(
                calculateMonthlyInstallment(totalPrice, selectedInstallments),
              )
            : undefined
        }
        buttonLabel={buttonLabel}
        onButtonClick={handleSubmit((data) => onSubmit(data))}
        disabled={disabledSubmit || !isValid || !isDirty}
      />
    </FormProvider>
  );
};
